/* eslint-disable import/no-extraneous-dependencies */
/* eslint-disable react/no-array-index-key */
/* eslint no-underscore-dangle: ["error", { "allow": [ "_id"] }] */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Grid,
  Pagination,
  Button,
  Stack,
  Divider,
  Alert,
  CircularProgress
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  Search as SearchIcon,
  OpenInNew as OpenInNewIcon,
  Notifications as NotificationsIcon,
  CheckCircle as CheckCircleIcon,
  Clear as ClearIcon,
  Refresh as RefreshIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import dateFormat from 'dateformat';
import Loader from '../../utils/errorPages/Loader';
import globalAction from '../../store/actions/GlobalAction';

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const { notificationData, loading } = useSelector((state) => state.global);
  const [notifications, setNotifications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    current_page: 1,
    last_page: 1,
    per_page: 10
  });

  useEffect(() => {
    if (notificationData) {
      setNotifications(notificationData.data || []);
      setPaginationData({
        total: notificationData.total || 0,
        current_page: notificationData.current_page || 1,
        last_page: notificationData.last_page || 1,
        per_page: notificationData.per_page || 10
      });
      setError(null);
    }
  }, [notificationData]);

  const loadNotifications = useCallback(
    async (page = 1, search = '', status = '') => {
      try {
        await dispatch(globalAction.getAllNotifications(page, search, status));
        setError(null);
      } catch (err) {
        setError('Failed to load notifications');
      }
    },
    [dispatch]
  );

  // Initial load
  useEffect(() => {
    loadNotifications(1, searchTerm, statusFilter);
  }, []);

  // Handle search and filter changes
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    loadNotifications(1, searchTerm, statusFilter);
  }, [loadNotifications, searchTerm, statusFilter]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch();
    }, 250);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, handleSearch]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    loadNotifications(page, searchTerm, statusFilter);
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (!notification?.id || !notification.is_unread) return;

    try {
      await dispatch(globalAction.readNotification(notification.id));
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, is_unread: false } : n))
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
      setError('Failed to mark notification as read');
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    const unreadCount = notifications.filter((n) => n.is_unread).length;
    if (unreadCount === 0) return;

    try {
      await dispatch(globalAction.readAllNotifications());
      setNotifications((prev) => prev.map((n) => ({ ...n, is_unread: false })));
      setError(null);
    } catch (err) {
      console.error('Error marking all as read:', err);
      setError('Failed to mark all notifications as read');
    }
  };

  // Handle refresh
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await loadNotifications(currentPage, searchTerm, statusFilter);
      setError(null);
    } catch (err) {
      console.error('Error refreshing notifications:', err);
      setError('Failed to refresh notifications');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Clear filters
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setCurrentPage(1);
    loadNotifications(1, '', 'all');
  };

  // Format date/time
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'Unknown';
    try {
      const date = new Date(dateTimeString);
      if (Number.isNaN(date.getTime())) return dateTimeString;

      const now = new Date();
      const diffInHours = Math.abs(now - date) / 36e5; // difference in hours

      // Less than 1 hour ago
      if (diffInHours < 1) {
        const diffInMinutes = Math.floor(diffInHours * 60);
        return `${diffInMinutes}m ago`;
      }

      // Less than 24 hours
      if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `${hours}h ago`;
      }

      // Yesterday
      if (diffInHours < 48) {
        return `Yesterday, ${dateFormat(date, 'hh:MM TT')}`;
      }

      // Within last 7 days
      if (diffInHours < 168) {
        return `${dateFormat(date, 'dddd, hh:MM TT')}`;
      }

      // Older dates
      return dateFormat(date, 'mmm dd, hh:MM TT');
    } catch (err) {
      console.error('Error formatting date:', err);
      return dateTimeString;
    }
  };

  // Get stats
  const unreadCount = notifications.filter((n) => n?.is_unread).length;

  if (loading && notifications.length === 0) {
    return <Loader />;
  }

  return (
    <Box className="cardWrapper" sx={{ width: '100%', py: 2, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                letterSpacing: '-0.5px'
              }}
            >
              Notifications
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: 'text.secondary',
                mt: 0.5,
                fontSize: '0.9rem'
              }}
            >
              {paginationData.total} total notifications
              {unreadCount > 0 && `, ${unreadCount} unread`}
            </Typography>
          </Box>
          {unreadCount > 0 && (
            <Button
              variant="contained"
              size="small"
              startIcon={<CheckCircleIcon />}
              onClick={handleMarkAllAsRead}
              sx={{
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              Mark All Read
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Card
          sx={{
            p: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            borderRadius: 2,
            bgcolor: 'background.paper',
            width: '100%'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setSearchTerm('')} size="small">
                        <ClearIcon sx={{ color: 'text.secondary' }} fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                  sx: {
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.200'
                    }
                  }
                }}
                sx={{ '& .MuiInputBase-root': { borderRadius: 1 } }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.9rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'grey.200'
                    }
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="unread">Unread</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <Button
                fullWidth
                size="small"
                onClick={clearFilters}
                disabled={!searchTerm && statusFilter === 'all'}
                sx={{
                  bgcolor: 'grey.100',
                  color: 'text.primary',
                  '&:hover': {
                    bgcolor: 'grey.200',
                    transform: 'translateY(-1px)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                Clear Filters
              </Button>
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button
                  size="small"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
                  sx={{
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    '&:hover': {
                      bgcolor: 'grey.200',
                      transform: 'translateY(-1px)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Card>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            bgcolor: 'error.light',
            color: 'error.contrastText',
            width: '100%'
          }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Notifications List */}
      <Card
        sx={{
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          borderRadius: 2,
          overflow: 'hidden',
          width: '100%'
        }}
      >
        <List sx={{ p: 0, width: '100%' }}>
          {notifications.length > 0 ? (
            notifications.map((notification, index) => (
              <React.Fragment key={notification?.id || index}>
                <ListItem
                  sx={{
                    py: 1,
                    px: 2,
                    bgcolor: notification?.is_unread
                      ? 'linear-gradient(90deg, rgba(25, 118, 210, 0.06) 0%, rgba(25, 118, 210, 0.02) 100%)'
                      : 'transparent',
                    borderLeft: notification?.is_unread ? '4px solid' : '4px solid transparent',
                    borderLeftColor: notification?.is_unread ? 'primary.main' : 'transparent',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: notification?.is_unread
                        ? 'rgba(25, 118, 210, 0.1)'
                        : 'rgba(0, 0, 0, 0.06)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    },
                    transition: 'all 0.2s ease',
                    minHeight: 48,
                    width: '100%'
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  {/* Status Icon Column */}
                  <Box
                    sx={{
                      width: 32,
                      display: 'flex',
                      alignItems: 'center',
                      mr: 1.5,
                      '& svg': {
                        transition: 'transform 0.2s ease'
                      },
                      '&:hover svg': {
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    {notification?.is_unread ? (
                      <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
                    ) : (
                      <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                    )}
                  </Box>
                  <ListItemAvatar sx={{ minWidth: 40, mr: 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: notification?.is_unread ? 'primary.main' : 'grey.300',
                        color: 'white',
                        width: 32,
                        height: 32,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.05)'
                        }
                      }}
                    >
                      <NotificationsIcon fontSize="small" />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: notification?.is_unread ? 'bold' : 'normal',
                              flex: 1
                            }}
                            dangerouslySetInnerHTML={{
                              __html: notification?.message || 'No message'
                            }}
                          />
                          {notification?.is_unread && (
                            <Typography
                              variant="caption"
                              sx={{
                                backgroundColor: 'primary.main',
                                color: 'white',
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.7rem'
                              }}
                            >
                              NEW
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDateTime(notification?.created_at)}
                          </Typography>
                          {notification?.type && notification?.reference_id && (
                            <Button
                              component={Link}
                              to={`${notification.type}${notification.reference_id}`}
                              size="small"
                              endIcon={<OpenInNewIcon />}
                              onClick={(e) => e.stopPropagation()}
                            >
                              View
                            </Button>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
                {index < notifications.length - 1 && (
                  <Divider sx={{ bgcolor: 'grey.100', mx: 2 }} />
                )}
              </React.Fragment>
            ))
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', width: '100%' }}>
              <NotificationsIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2, opacity: 0.7 }} />
              <Typography
                variant="h6"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 500,
                  mb: 1
                }}
              >
                No notifications found
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                  opacity: 0.8
                }}
              >
                {searchTerm || statusFilter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : "You're all caught up!"}
              </Typography>
            </Box>
          )}
        </List>

        {/* Pagination */}
        {paginationData.last_page > 1 && (
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'center',
              borderTop: 1,
              borderColor: 'grey.100',
              bgcolor: 'grey.50',
              width: '100%'
            }}
          >
            <Pagination
              count={paginationData.last_page}
              page={paginationData.current_page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 1,
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  },
                  transition: 'all 0.2s ease'
                }
              }}
            />
          </Box>
        )}

        {/* Footer */}
        {notifications.length > 0 && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'grey.50',
              textAlign: 'center',
              borderTop: 1,
              borderColor: 'grey.100',
              width: '100%'
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontSize: '0.8rem',
                opacity: 0.8
              }}
            >
              Showing {notifications.length} of {paginationData.total} notifications Page (
              {paginationData.current_page} of {paginationData.last_page})
            </Typography>
          </Box>
        )}
      </Card>
    </Box>
  );
}
